import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { User } from '../types';
import { supabase, auth, getCurrentProfile } from '../services/supabaseClient';
import { handleOAuthProfile } from '../services/oauthProfileService';
import { apiService } from '../services/api';
import { queryClient } from '../services/queryClient';
import type { Session, AuthChangeEvent, AuthResponse } from '@supabase/supabase-js';
import type { Tables } from '../types/supabase';
import { trackEvent, AnalyticsEvents } from '../analytics';

type Profile = Tables<'profiles'>;

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (email: string, password: string, metadata?: { first_name?: string; last_name?: string; phone_number?: string; collaborating_lawyers_count?: number }) => Promise<{ data: AuthResponse['data'] | null; error: AuthResponse['error'] | null }>;
  refreshUser: () => Promise<void>;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
  isSuperadmin: () => boolean;
  canAccess: (requiredRole: string) => boolean;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Map Supabase profile to our User type
function mapProfileToUser(profile: Profile, email: string): User {
  // Map Supabase roles to frontend roles
  const roleMap: Record<string, 'regular' | 'admin' | 'superadmin'> = {
    'superadmin': 'superadmin',
    'admin': 'admin',
    'lawyer': 'regular',
    'assistant': 'regular',
    'client': 'regular',
  };

  return {
    id: profile.id,
    email: email,
    name: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || email,
    role: roleMap[profile.role || 'lawyer'] || 'regular',
    organizationId: profile.organization_id,
    isActive: profile.is_active ?? true,
    isTestAccount: profile.is_test_account ?? false,
    createdAt: profile.created_at || new Date().toISOString(),
    updatedAt: profile.updated_at || new Date().toISOString(),
    preferences: profile.preferences as Record<string, any> | undefined,
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from Supabase session on mount.
  // Use onAuthStateChange(INITIAL_SESSION) only — do not call getSession(), which can hang
  // or throw AbortError (LockManager) and blocks login. Supabase docs recommend this pattern.
  useEffect(() => {
    let mounted = true;
    let initialHandled = false;

    if (!auth) {
      setIsLoading(false);
      return () => { mounted = false; };
    }

    const doneLoading = () => {
      if (mounted) setIsLoading(false);
    };

    const fallbackTimeout = setTimeout(() => {
      if (mounted && !initialHandled) {
        initialHandled = true;
        doneLoading();
      }
    }, 5000);

    const { data: { subscription } } = auth.onAuthStateChange(
      async (event: AuthChangeEvent, session: Session | null) => {
        try {
          if (event === 'INITIAL_SESSION') {
            initialHandled = true;
            if (!mounted) return;
            setToken(session?.access_token ?? null);
            doneLoading();
            if (session) {
              setTimeout(() => loadUserProfile(session).catch(() => {}), 0);
            }
            return;
          }
          if (event === 'SIGNED_IN' && session && mounted) {
            // Clear all cached data on login to prevent cross-account data leakage
            queryClient.clear();
            apiService.cache.clearAll();
            setToken(session.access_token);
            doneLoading();
            setTimeout(() => loadUserProfile(session).catch(() => {}), 0);
          } else if (event === 'PASSWORD_RECOVERY' && session && mounted) {
            // Password recovery: set the token so updatePassword() works,
            // but don't load the full user profile (user is only here to reset).
            setToken(session.access_token);
            doneLoading();
          } else if (event === 'SIGNED_OUT' && mounted) {
            // Session expired or refresh token invalid - clear state
            setUser(null);
            setToken(null);
            apiService.cache.clearAll();
            doneLoading();
          } else if (event === 'TOKEN_REFRESHED' && session && mounted) {
            setToken(session.access_token);
          }
        } catch (error: any) {
          // Handle refresh token errors gracefully
          if (error?.message?.includes('Invalid Refresh Token') || error?.message?.includes('Refresh Token Not Found')) {
            console.warn('Refresh token invalid, clearing session');
            if (mounted) {
              setUser(null);
              setToken(null);
              doneLoading();
            }
          } else {
            console.error('Auth state change error:', error);
          }
        }
      }
    );

    return () => {
      mounted = false;
      initialHandled = true;
      clearTimeout(fallbackTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const loadUserProfile = async (session: Session) => {
    try {
      let profile = await getCurrentProfile();
      
      // If no profile exists, try to create one from OAuth metadata
      if (!profile && session.user) {
        try {
          await handleOAuthProfile(session.user);
          // Retry fetching profile after creation
          profile = await getCurrentProfile();
        } catch (oauthError) {
          console.error('Error handling OAuth profile:', oauthError);
        }
      }
      
      if (profile && session.user.email) {
        const mappedUser = mapProfileToUser(profile, session.user.email);

        // Sign out deactivated users
        if (mappedUser.isActive === false) {
          console.warn('User is deactivated, signing out:', mappedUser.email);
          await auth.signOut();
          setUser(null);
          setToken(null);
          return;
        }

        setUser(mappedUser);
      } else {
        console.warn('No profile found for user:', session.user.email);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      // Don't throw - allow app to continue even if profile load fails
    }
  };

  const login = async (email: string, password: string) => {
    const { data, error } = await auth.signIn(email, password);

    if (error) {
      trackEvent({ ...AnalyticsEvents.LOGIN_FAILURE, label: error.message });
      throw new Error(error.message);
    }

    if (data.session) {
      // Clear all cached data on login to prevent cross-account data leakage
      queryClient.clear();
      apiService.cache.clearAll();
      setToken(data.session.access_token);
      await loadUserProfile(data.session);
      trackEvent(AnalyticsEvents.LOGIN_SUCCESS);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    metadata?: { first_name?: string; last_name?: string; phone_number?: string; collaborating_lawyers_count?: number }
  ) => {
    const { data, error } = await auth.signUp(email, password, metadata);

    // Return the data and error so the caller can handle it
    // Note: User may need to verify email depending on Supabase settings
    if (!error && data?.session) {
      setToken(data.session.access_token);
      await loadUserProfile(data.session);
      trackEvent(AnalyticsEvents.SIGNUP_SUCCESS);
    }

    return { data, error };
  };

  const logout = async () => {
    try {
      trackEvent(AnalyticsEvents.LOGOUT);
      await auth.signOut();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setToken(null);
      setUser(null);
      apiService.cache.clearAll();
    }
  };

  const refreshUser = async () => {
    try {
      const { data: { session }, error } = await auth.getSession();
      if (error) {
        // If session fetch fails (e.g., invalid refresh token), clear state
        if (error.message?.includes('Invalid Refresh Token') || error.message?.includes('Refresh Token Not Found')) {
          console.warn('Refresh token invalid, clearing session');
          setUser(null);
          setToken(null);
          return;
        }
        throw error;
      }
      if (session) {
        await loadUserProfile(session);
      } else {
        // No session - clear state
        setUser(null);
        setToken(null);
      }
    } catch (error: any) {
      console.error('Error refreshing user:', error);
      // Clear state on any error to prevent stale sessions
      setUser(null);
      setToken(null);
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await auth.resetPassword(email);
    if (error) {
      throw new Error(error.message);
    }
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await auth.updatePassword(newPassword);
    if (error) {
      throw new Error(error.message);
    }
  };

  const loginWithGoogle = async () => {
    const { error } = await auth.signInWithGoogle();
    if (error) {
      throw new Error(error.message);
    }
    // Track OAuth initiation (success will be tracked after redirect in AuthCallback)
    trackEvent({ ...AnalyticsEvents.LOGIN_SUCCESS, label: 'google_oauth' });
    // OAuth redirect will happen automatically
    // User will be redirected back to /auth/callback after Google authentication
  };

  const loginWithApple = async () => {
    const { error } = await auth.signInWithApple();
    if (error) {
      throw new Error(error.message);
    }
    // Track OAuth initiation (success will be tracked after redirect in AuthCallback)
    trackEvent({ ...AnalyticsEvents.LOGIN_SUCCESS, label: 'apple_oauth' });
    // OAuth redirect will happen automatically
    // User will be redirected back to /auth/callback after Apple authentication
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    return user.role === role;
  };

  const isAdmin = (): boolean => {
    return hasRole('admin') || hasRole('superadmin');
  };

  const isSuperadmin = (): boolean => {
    return hasRole('superadmin');
  };

  const canAccess = (requiredRole: string): boolean => {
    if (!user) return false;
    
    const roleHierarchy: Record<string, number> = {
      regular: 1,
      admin: 2,
      superadmin: 3,
    };

    const userLevel = roleHierarchy[user.role] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;

    return userLevel >= requiredLevel;
  };

  // Memoize isAuthenticated to prevent unnecessary re-renders
  const isAuthenticated = useMemo(() => !!user && !!token, [user, token]);

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    logout,
    signUp,
    refreshUser,
    hasRole,
    isAdmin,
    isSuperadmin,
    canAccess,
    resetPassword,
    updatePassword,
    loginWithGoogle,
    loginWithApple,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
